import { useEffect, useMemo, useRef, useState } from "react";

export interface ComboboxOption {
  value: number;
  label: string;
}

interface ComboboxProps {
  label: string;
  value: number | "";
  onChange: (v: number | "") => void;
  options: ComboboxOption[];
  placeholder?: string;
}

export function Combobox({
  label,
  value,
  onChange,
  options,
  placeholder = "Todos",
}: ComboboxProps) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);

  const selectedLabel = useMemo(
    () =>
      value === ""
        ? ""
        : options.find((o) => o.value === value)?.label ?? "",
    [value, options]
  );

  useEffect(() => {
    if (!open) {
      setQuery(selectedLabel);
    }
  }, [selectedLabel, open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [query, options]);

  useEffect(() => {
    setHighlight(0);
  }, [query, open]);

  useEffect(() => {
    function onDocMouseDown(e: MouseEvent) {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, []);

  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector<HTMLElement>(
      `[data-idx="${highlight}"]`
    );
    el?.scrollIntoView({ block: "nearest" });
  }, [highlight, open]);

  function select(opt: ComboboxOption) {
    onChange(opt.value);
    setQuery(opt.label);
    setOpen(false);
    inputRef.current?.blur();
  }

  function clear() {
    onChange("");
    setQuery("");
    setOpen(false);
    inputRef.current?.focus();
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      setHighlight((h) => Math.min(filtered.length - 1, h + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(0, h - 1));
    } else if (e.key === "Enter") {
      if (!open) return;
      e.preventDefault();
      const opt = filtered[highlight];
      if (opt) select(opt);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      setQuery(selectedLabel);
      inputRef.current?.blur();
    } else if (e.key === "Backspace" && query === "" && value !== "") {
      e.preventDefault();
      clear();
    }
  }

  return (
    <div className="relative" ref={containerRef}>
      <label className="block text-sm">
        <span className="block text-slate-600 mb-1">{label}</span>
        <div className="relative">
          <input
            ref={inputRef}
            type="text"
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={open}
            value={query}
            placeholder={placeholder}
            onFocus={() => {
              setOpen(true);
              setQuery("");
            }}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onKeyDown={onKeyDown}
            className="w-full rounded border border-slate-300 bg-white px-3 py-1.5 pr-8 focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
          {value !== "" ? (
            <button
              type="button"
              tabIndex={-1}
              onClick={clear}
              aria-label="Limpar seleção"
              className="absolute inset-y-0 right-1 my-auto h-6 w-6 flex items-center justify-center rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100"
            >
              ×
            </button>
          ) : (
            <span
              aria-hidden="true"
              className="absolute inset-y-0 right-2 my-auto flex items-center text-slate-400 text-xs"
            >
              ▾
            </span>
          )}
        </div>
      </label>
      {open && (
        <ul
          ref={listRef}
          role="listbox"
          className="absolute z-20 left-0 right-0 mt-1 max-h-60 overflow-auto rounded border border-slate-200 bg-white shadow-lg"
        >
          {filtered.length === 0 ? (
            <li className="px-3 py-2 text-slate-500 text-sm">
              Nenhum resultado
            </li>
          ) : (
            filtered.map((opt, idx) => (
              <li
                key={opt.value}
                data-idx={idx}
                role="option"
                aria-selected={value === opt.value}
                onMouseDown={(e) => {
                  e.preventDefault();
                  select(opt);
                }}
                onMouseEnter={() => setHighlight(idx)}
                className={[
                  "px-3 py-1.5 cursor-pointer text-sm",
                  idx === highlight
                    ? "bg-brand-50 text-brand-900"
                    : "text-slate-700",
                  value === opt.value ? "font-medium" : "",
                ].join(" ")}
              >
                {opt.label}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
